import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, inputCls, Modal, fmtDate, fmtDay } from "@/components/crm/kit";
import { ClientStatusPill, SERVICE_TONE } from "./Clients";
import {
  ArrowLeft, Building2, Plus, Trash2, Share2, Activity as ActivityIcon, Target, Layers, Loader2,
} from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState({ client_statuses: [], service_statuses: [], services: [], users: [] });
  const [data, setData] = useState(null);
  const [showSvc, setShowSvc] = useState(false);
  const [showRef, setShowRef] = useState(false);
  const [svcForm, setSvcForm] = useState({ service_name: "", status: "Not Started", partner: "", start_date: "", owner_id: "" });
  const [refForm, setRefForm] = useState({ service: "", receiving_partner: "", owner_id: "", referral_date: "", next_followup_date: "", notes: "" });

  const load = useCallback(async () => {
    const r = await api.get(`/crm/clients/${id}`);
    setData(r.data);
  }, [id]);

  useEffect(() => {
    api.get("/crm/hudlup/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const c = data.client;

  const patchClient = async (patch) => {
    try { await api.patch(`/crm/clients/${id}`, patch); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const milestoneFor = (name) => (meta.services.find((s) => s.name === name) || {}).milestone_label;
  const svcStatusOptions = (name) => {
    const m = milestoneFor(name);
    return m && !meta.service_statuses.includes(m) ? [...meta.service_statuses, m] : meta.service_statuses;
  };

  const addService = async () => {
    if (!svcForm.service_name) return toast.error("Choose a service");
    try { await api.post(`/crm/clients/${id}/services`, svcForm); toast.success("Service added"); setShowSvc(false); setSvcForm({ service_name: "", status: "Not Started", partner: "", start_date: "", owner_id: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const patchService = async (sid, patch) => {
    try { await api.patch(`/crm/clients/${id}/services/${sid}`, patch); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delService = async (sid) => { try { await api.delete(`/crm/clients/${id}/services/${sid}`); toast.success("Service removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const addReferral = async () => {
    try {
      await api.post("/crm/referrals", { ...refForm, referring_client_id: id, referring_client_name: c.name });
      toast.success("Referral created"); setShowRef(false);
      setRefForm({ service: "", receiving_partner: "", owner_id: "", referral_date: "", next_followup_date: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6">
      <button data-testid="cd-back" onClick={() => nav("/crm/clients")} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-primary"><ArrowLeft className="h-4 w-4" /> Clients & Services</button>

      {/* Header */}
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Building2 className="h-6 w-6" /></span>
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="cd-name">{c.name}</h1>
              <p className="text-sm text-slate-500">{c.type || "—"} · {c.city || "?"}{c.state ? `, ${c.state}` : ""}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">Client Status</label>
              <select data-testid="cd-client-status" value={c.client_status || "Not Started"} onChange={(e) => patchClient({ client_status: e.target.value })} className={`${inputCls} font-semibold`}>
                {meta.client_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400">Owner</label>
              <select data-testid="cd-owner" value={c.owner_id || ""} onChange={(e) => patchClient({ owner_id: e.target.value })} className={inputCls}>
                <option value="">Unassigned</option>
                {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
        </div>
        {data.opportunities.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400"><Target className="h-3.5 w-3.5" /> Sales pipeline (separate)</span>
            {data.opportunities.map((o) => (
              <span key={o.opp_number} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">{o.opp_number} · {o.stage}{o.status === "won" ? " · Won" : ""}</span>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Services + Referrals */}
        <div className="space-y-6 lg:col-span-2">
          {/* Services */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="cd-services">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Layers className="h-5 w-5 text-primary" /> Services</h2>
              <Btn variant="ghost" onClick={() => setShowSvc(true)} data-testid="cd-add-service"><Plus className="h-4 w-4" /> Add service</Btn>
            </div>
            {(c.services || []).length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">No services yet. Add 340B, RPM, infusion or another service — each progresses independently.</p>
            ) : (
              <div className="space-y-3">
                {c.services.map((s) => (
                  <div key={s.id} data-testid={`cd-service-${s.id}`} className="rounded-xl border border-slate-200 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-display font-bold text-slate-800">{s.service_name}</p>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${SERVICE_TONE[s.status] || "bg-slate-100 text-slate-500"}`}>{s.status}</span>
                        <button onClick={() => delService(s.id)} data-testid={`cd-del-service-${s.id}`} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                      <label className="block"><span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Status</span>
                        <select data-testid={`cd-svc-status-${s.id}`} value={s.status} onChange={(e) => patchService(s.id, { status: e.target.value })} className={inputCls}>
                          {svcStatusOptions(s.service_name).map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </label>
                      <label className="block"><span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Partner / provider</span>
                        <input defaultValue={s.partner || ""} onBlur={(e) => { if (e.target.value !== (s.partner || "")) patchService(s.id, { partner: e.target.value }); }} className={inputCls} placeholder="e.g. Caregenix" />
                      </label>
                      <label className="block"><span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Start date</span>
                        <input type="date" defaultValue={s.start_date || ""} onBlur={(e) => { if (e.target.value !== (s.start_date || "")) patchService(s.id, { start_date: e.target.value }); }} className={inputCls} />
                      </label>
                      <label className="block"><span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Owner</span>
                        <select value={s.owner_id || ""} onChange={(e) => patchService(s.id, { owner_id: e.target.value })} className={inputCls}>
                          <option value="">—</option>
                          {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </label>
                    </div>
                    <p className="mt-2 text-[11px] text-slate-400">Updated {fmtDate(s.last_updated)}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Referrals */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="cd-referrals">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Share2 className="h-5 w-5 text-primary" /> Referrals</h2>
              <Btn variant="ghost" onClick={() => setShowRef(true)} data-testid="cd-add-referral"><Plus className="h-4 w-4" /> New referral</Btn>
            </div>
            {data.referrals.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">No referrals yet for this client.</p>
            ) : (
              <div className="space-y-2">
                {data.referrals.map((r) => (
                  <button key={r.id} onClick={() => nav(`/crm/referral-tracking?ref=${r.id}`)} data-testid={`cd-ref-${r.id}`} className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-left hover:bg-slate-50">
                    <span className="font-mono text-[11px] font-bold text-slate-400">{r.ref_number}</span>
                    <span className="flex-1 truncate text-sm font-semibold text-slate-700">{r.service || "—"} → {r.receiving_partner || "partner"}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">{r.status}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Activity timeline */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="cd-activity">
          <h2 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><ActivityIcon className="h-5 w-5 text-primary" /> Activity</h2>
          {data.activity.length === 0 ? (
            <p className="text-sm text-slate-400">No activity yet.</p>
          ) : (
            <div className="space-y-3">
              {data.activity.map((a) => (
                <div key={a.id} className="border-l-2 border-slate-200 pl-3">
                  <p className="text-sm text-slate-700">{a.summary}</p>
                  <p className="text-[11px] text-slate-400">{a.by_name} · {fmtDate(a.at)}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Add service modal */}
      <Modal open={showSvc} onClose={() => setShowSvc(false)} title="Add a service">
        <Field label="Service *">
          <select data-testid="cd-svc-name" className={inputCls} value={svcForm.service_name} onChange={(e) => setSvcForm({ ...svcForm, service_name: e.target.value })}>
            <option value="">Choose a service…</option>
            {meta.services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <select data-testid="cd-svc-new-status" className={inputCls} value={svcForm.status} onChange={(e) => setSvcForm({ ...svcForm, status: e.target.value })}>
              {svcStatusOptions(svcForm.service_name).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Partner / provider"><input className={inputCls} value={svcForm.partner} onChange={(e) => setSvcForm({ ...svcForm, partner: e.target.value })} placeholder="e.g. Caregenix" /></Field>
          <Field label="Start date"><input type="date" className={inputCls} value={svcForm.start_date} onChange={(e) => setSvcForm({ ...svcForm, start_date: e.target.value })} /></Field>
          <Field label="Owner">
            <select className={inputCls} value={svcForm.owner_id} onChange={(e) => setSvcForm({ ...svcForm, owner_id: e.target.value })}>
              <option value="">—</option>
              {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        </div>
        <Btn onClick={addService} data-testid="cd-svc-save" className="mt-2 w-full justify-center">Add service</Btn>
      </Modal>

      {/* New referral modal */}
      <Modal open={showRef} onClose={() => setShowRef(false)} title={`New referral · ${c.name}`}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Service">
            <select data-testid="cd-ref-service" className={inputCls} value={refForm.service} onChange={(e) => setRefForm({ ...refForm, service: e.target.value })}>
              <option value="">—</option>
              {meta.services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Receiving partner"><input data-testid="cd-ref-partner" className={inputCls} value={refForm.receiving_partner} onChange={(e) => setRefForm({ ...refForm, receiving_partner: e.target.value })} /></Field>
          <Field label="Owner">
            <select className={inputCls} value={refForm.owner_id} onChange={(e) => setRefForm({ ...refForm, owner_id: e.target.value })}>
              <option value="">Me</option>
              {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Referral date"><input type="date" className={inputCls} value={refForm.referral_date} onChange={(e) => setRefForm({ ...refForm, referral_date: e.target.value })} /></Field>
          <Field label="Next follow-up"><input type="date" className={inputCls} value={refForm.next_followup_date} onChange={(e) => setRefForm({ ...refForm, next_followup_date: e.target.value })} /></Field>
        </div>
        <Field label="Notes"><textarea className={inputCls} rows={2} value={refForm.notes} onChange={(e) => setRefForm({ ...refForm, notes: e.target.value })} /></Field>
        <Btn onClick={addReferral} data-testid="cd-ref-save" className="mt-2 w-full justify-center">Create referral</Btn>
      </Modal>
    </div>
  );
}
